import { Tile, tallGrassPasses } from '../sim/grid';

/** Visual/content identity of a tile. Many ids share one sim behavior (`kind`). */
export type TileId =
  | 'grass'
  | 'tallGrass'
  | 'path'
  | 'flowerBed'
  | 'mud'
  | 'sticks'
  | 'water'
  | 'rock'
  | 'birdBath'
  | 'tree'
  | 'shrub'
  | 'gnome'
  | 'house'
  | 'sand'
  | 'bench'
  | 'lampPost'
  | 'bin'
  | 'barn'
  | 'fence'
  | 'wheat'
  | 'hayBale'
  | 'crops'
  | 'hedge'
  | 'trough'
  | 'hose'
  | 'dogHouse'
  | 'sprinkler'
  | 'judgesTable'
  | 'pen'
  | 'penGate'
  | 'roboDock'
  | 'green'
  | 'flag'
  | 'ballWasher'
  | 'bigTree'
  | 'headstone'
  | 'lantern'
  | 'thorns'
  | 'deadTree'
  | 'log'
  | 'cobble'
  | 'bridge'
  | 'urn'
  | 'statue'
  | 'fountain'
  | 'topiary'
  | 'mazeHedge'
  | 'fieldLine'
  | 'goal'
  | 'cornerFlag'
  | 'cone'
  | 'meadowGrass'
  | 'beehive'
  | 'showLawn';

/** How the generator uses a tile. */
export type Placement =
  /** Laid down as terrain: patches or strips. */
  | 'terrain'
  /** Single obstacles scattered by the difficulty budget. */
  | 'scatter'
  /** Built by the yard-layout stage (house, driveway). */
  | 'structure'
  /** Laid as a line snaking in from the lawn edge (garden hoses). */
  | 'line';

export interface TileDef {
  id: TileId;
  name: string;
  /** Character in ASCII level rows. */
  char: string;
  kind: Tile;
  /** First campaign level where the generator may use it (Infinity = hand-placed only). */
  unlockLevel: number;
  /** Difficulty budget cost per tile placed by the generator. */
  cost: number;
  placement: Placement;
}

/** The tile catalog (docs/GDD.md §6). Unlock levels and costs are starting points for tuning. */
export const TILES: readonly TileDef[] = [
  {
    id: 'grass',
    name: 'Grass',
    char: '.',
    kind: Tile.Grass,
    unlockLevel: 1,
    cost: 0,
    placement: 'terrain',
  },
  {
    id: 'path',
    name: 'Path',
    char: '_',
    kind: Tile.Ground,
    unlockLevel: 3,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'rock',
    name: 'Rock',
    char: '#',
    kind: Tile.Obstacle,
    unlockLevel: 2,
    cost: 3,
    placement: 'scatter',
  },
  {
    id: 'birdBath',
    name: 'Bird Bath',
    char: 'B',
    kind: Tile.Obstacle,
    unlockLevel: 4,
    cost: 3,
    placement: 'scatter',
  },
  {
    id: 'flowerBed',
    name: 'Flower Bed',
    char: '*',
    kind: Tile.FlowerBed,
    unlockLevel: 5,
    cost: 2,
    placement: 'terrain',
  },
  {
    id: 'tree',
    name: 'Tree',
    char: 'T',
    kind: Tile.Obstacle,
    unlockLevel: 6,
    cost: 3,
    placement: 'scatter',
  },
  {
    // The big house's back deck (owner, 2026-10-07; once a little house in the yard).
    id: 'house',
    name: 'Back Deck',
    char: 'H',
    kind: Tile.Obstacle,
    unlockLevel: 7,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'tallGrass',
    name: 'Tall Grass',
    char: ',',
    kind: Tile.TallGrass,
    unlockLevel: 9,
    cost: 1,
    placement: 'terrain',
  },
  {
    id: 'shrub',
    name: 'Shrub',
    char: 'h',
    kind: Tile.Hedge,
    unlockLevel: 9,
    cost: 3,
    placement: 'scatter',
  },
  {
    id: 'gnome',
    name: 'Garden Gnome',
    char: 'g',
    kind: Tile.Obstacle,
    unlockLevel: 11,
    cost: 4,
    placement: 'scatter',
  },
  {
    id: 'sticks',
    name: 'Sticks',
    char: 'x',
    kind: Tile.Brush,
    unlockLevel: 13,
    cost: 4,
    placement: 'scatter',
  },
  {
    id: 'mud',
    name: 'Mud',
    char: '~',
    kind: Tile.Mud,
    unlockLevel: 16,
    cost: 2,
    placement: 'terrain',
  },
  {
    id: 'water',
    name: 'Pond',
    char: 'w',
    kind: Tile.Obstacle,
    unlockLevel: 19,
    cost: 2,
    placement: 'terrain',
  },
  // Neighborhood Park (21-40)
  {
    id: 'sand',
    name: 'Sandpit',
    char: 's',
    kind: Tile.Ground,
    unlockLevel: 21,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'bench',
    name: 'Park Bench',
    char: 'b',
    kind: Tile.Obstacle,
    unlockLevel: 21,
    cost: 3,
    placement: 'scatter',
  },
  {
    id: 'lampPost',
    name: 'Lamp Post',
    char: 'l',
    kind: Tile.Obstacle,
    unlockLevel: 23,
    cost: 3,
    placement: 'scatter',
  },
  {
    id: 'bin',
    name: 'Trash Bin',
    char: 'n',
    kind: Tile.Obstacle,
    unlockLevel: 26,
    cost: 3,
    placement: 'scatter',
  },
  // Farm (41-60)
  {
    id: 'barn',
    name: 'Barn',
    char: 'R',
    kind: Tile.Obstacle,
    unlockLevel: 41,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'fence',
    name: 'Fence',
    char: 'f',
    kind: Tile.Obstacle,
    unlockLevel: 41,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'wheat',
    name: 'Wheat',
    char: 'v',
    kind: Tile.TallGrass,
    unlockLevel: 41,
    cost: 1,
    placement: 'terrain',
  },
  {
    id: 'hayBale',
    name: 'Hay Bale',
    char: 'y',
    kind: Tile.Obstacle,
    unlockLevel: 41,
    cost: 3,
    placement: 'scatter',
  },
  {
    id: 'crops',
    name: 'Vegetable Patch',
    char: 'c',
    kind: Tile.FlowerBed,
    unlockLevel: 42,
    cost: 2,
    placement: 'terrain',
  },
  {
    id: 'hedge',
    name: 'Hedgerow',
    char: 'e',
    kind: Tile.Hedge,
    unlockLevel: 43,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'trough',
    name: 'Water Trough',
    char: 'u',
    kind: Tile.Obstacle,
    unlockLevel: 46,
    cost: 3,
    placement: 'scatter',
  },
  {
    id: 'hose',
    name: 'Garden Hose',
    char: 'z',
    kind: Tile.Hose,
    unlockLevel: 22,
    cost: 0,
    placement: 'line',
  },
  {
    id: 'dogHouse',
    name: 'Dog House',
    char: 'K',
    kind: Tile.Obstacle,
    unlockLevel: 27,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'sprinkler',
    name: 'Sprinkler',
    char: 'q',
    kind: Tile.Sprinkler,
    unlockLevel: 31,
    cost: 0,
    placement: 'structure',
  },
  {
    // Lawn Art: the show lawn round the picture, kept perfect already (nothing to mow there, and
    // driving over it spoils nothing).
    id: 'showLawn',
    name: 'Show Lawn',
    char: '^',
    kind: Tile.Ground,
    unlockLevel: 40,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'judgesTable',
    name: "Judges' Table",
    char: 'J',
    kind: Tile.Ground,
    unlockLevel: 40,
    cost: 0,
    placement: 'structure',
  },
  {
    // Boss herding: the fenced sheep pen (the mower can't go in) and the open gate in front of it.
    id: 'pen',
    name: 'Sheep Pen',
    char: 'P',
    kind: Tile.Obstacle,
    unlockLevel: 60,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'penGate',
    name: 'Pen Gate',
    char: 'G',
    kind: Tile.Ground,
    unlockLevel: 60,
    cost: 0,
    placement: 'structure',
  },
  {
    // Boss rival: the robot mower's charging dock, where it sets off from.
    id: 'roboDock',
    name: 'Robot Dock',
    char: 'D',
    kind: Tile.Ground,
    unlockLevel: 80,
    cost: 0,
    placement: 'structure',
  },
  {
    // The Golf Course: a putting green (keep off: driving on it costs coins, like a flower bed),
    // its flag pin, and ball washers by the tees.
    id: 'green',
    name: 'Putting Green',
    char: 'o',
    kind: Tile.FlowerBed,
    unlockLevel: 61,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'flag',
    name: 'Flag Pin',
    char: 'F',
    kind: Tile.Obstacle,
    unlockLevel: 61,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'ballWasher',
    name: 'Ball Washer',
    char: 'W',
    kind: Tile.Obstacle,
    unlockLevel: 61,
    cost: 2,
    placement: 'scatter',
  },
  // Old Cemetery Grounds (81-100)
  {
    id: 'headstone',
    name: 'Headstone',
    char: 'i',
    kind: Tile.Obstacle,
    unlockLevel: 81,
    cost: 3,
    placement: 'scatter',
  },
  {
    id: 'lantern',
    name: 'Old Lantern',
    char: 'L',
    kind: Tile.Obstacle,
    unlockLevel: 81,
    cost: 3,
    placement: 'scatter',
  },
  {
    // Brambles and thistles: keep off, or a tire goes flat (lm-b0b).
    id: 'thorns',
    name: 'Thorn Patch',
    char: '%',
    kind: Tile.Thorns,
    unlockLevel: 83,
    cost: 2,
    placement: 'terrain',
  },
  {
    // An old dead tree that creaks, then falls (the level's `falls` say when and which way).
    id: 'deadTree',
    name: 'Dead Tree',
    char: 'X',
    kind: Tile.Obstacle,
    unlockLevel: 88,
    cost: 0,
    placement: 'structure',
  },
  {
    // Where a dead tree came down (made in play, never placed).
    id: 'log',
    name: 'Fallen Log',
    char: '=',
    kind: Tile.Obstacle,
    unlockLevel: Number.POSITIVE_INFINITY,
    cost: 0,
    placement: 'structure',
  },
  // Grand Estate (101-120)
  {
    id: 'cobble',
    name: 'Cobblestone Walk',
    char: 'C',
    kind: Tile.Ground,
    unlockLevel: 101,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'statue',
    name: 'Statue',
    char: 'A',
    kind: Tile.Obstacle,
    unlockLevel: 101,
    cost: 4,
    placement: 'structure',
  },
  {
    id: 'fountain',
    name: 'Fountain',
    char: 'O',
    kind: Tile.Obstacle,
    unlockLevel: 101,
    cost: 0,
    placement: 'structure',
  },
  {
    // A clipped topiary ball: solid, but the hedge trimmer can take it down.
    id: 'topiary',
    name: 'Topiary',
    char: 't',
    kind: Tile.Hedge,
    unlockLevel: 101,
    cost: 3,
    placement: 'scatter',
  },
  {
    // A bridge over the river: drivable, nothing to mow.
    id: 'bridge',
    name: 'Bridge',
    char: '+',
    kind: Tile.Ground,
    unlockLevel: 103,
    cost: 0,
    placement: 'structure',
  },
  {
    // A planted urn on the patio: drivable, but the mower knocks it over (coins).
    id: 'urn',
    name: 'Garden Urn',
    char: 'U',
    kind: Tile.Obstacle,
    unlockLevel: 105,
    cost: 0,
    placement: 'structure',
  },
  {
    // The Hedge Maze's walls: tall and thick, no trimming through them.
    id: 'mazeHedge',
    name: 'Maze Hedge',
    char: 'M',
    kind: Tile.Obstacle,
    unlockLevel: 120,
    cost: 0,
    placement: 'structure',
  },
  // Stadium (121-140)
  {
    // Grass with a chalk line on it (the pitch markings): mowed like any grass.
    id: 'fieldLine',
    name: 'Pitch Line',
    char: ':',
    kind: Tile.Grass,
    unlockLevel: 121,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'goal',
    name: 'Goal',
    char: 'Q',
    kind: Tile.Obstacle,
    unlockLevel: 121,
    cost: 0,
    placement: 'structure',
  },
  {
    id: 'cornerFlag',
    name: 'Corner Flag',
    char: 'r',
    kind: Tile.Obstacle,
    unlockLevel: 121,
    cost: 0,
    placement: 'structure',
  },
  {
    // A training cone: drivable, but the mower knocks it flying (coins).
    id: 'cone',
    name: 'Training Cone',
    char: 'a',
    kind: Tile.Obstacle,
    unlockLevel: 123,
    cost: 3,
    placement: 'scatter',
  },
  {
    // A big old tree: the trunk is an obstacle; its crown (renderer only) spreads over the lawn
    // two tiles round, hiding what is under it until the mower drives beneath (lm-t5v.12).
    id: 'bigTree',
    name: 'Big Tree',
    char: 'Y',
    kind: Tile.Obstacle,
    unlockLevel: 101,
    cost: 0,
    placement: 'structure',
  },
  // Rolling Meadows (141-160)
  {
    // Wildflower meadow grass: mowed like any grass, but a patch left half mowed grows back.
    id: 'meadowGrass',
    name: 'Meadow Grass',
    char: 'm',
    kind: Tile.Grass,
    unlockLevel: 141,
    cost: 0,
    placement: 'terrain',
  },
  {
    // A straw beehive on its stand: drive into it and it's smashed, the bees sting a heart away
    // and it costs coins. Lawns never need one smashed.
    id: 'beehive',
    name: 'Beehive',
    char: 'k',
    kind: Tile.Hive,
    unlockLevel: 151,
    cost: 4,
    placement: 'scatter',
  },
];

/** How long a half-mowed meadow patch waits before growing back, in tiles of mower travel. */
export const MEADOW_REGROW_TILES = 8;

/** The meadow patches of a map: each connected group of meadow grass (row-major indices). */
export function meadowPatches(art: readonly TileId[], width: number): number[][] {
  const seen = new Uint8Array(art.length);
  const patches: number[][] = [];
  for (let i = 0; i < art.length; i++) {
    if (art[i] !== 'meadowGrass' || seen[i]) continue;
    const patch: number[] = [];
    const queue = [i];
    seen[i] = 1;
    while (queue.length > 0) {
      const j = queue.pop()!;
      patch.push(j);
      const x = j % width;
      for (const k of [j - width, j + width, x > 0 ? j - 1 : -1, x < width - 1 ? j + 1 : -1]) {
        if (k >= 0 && k < art.length && !seen[k] && art[k] === 'meadowGrass') {
          seen[k] = 1;
          queue.push(k);
        }
      }
    }
    patches.push(patch.sort((a, b) => a - b));
  }
  return patches;
}

export const TILE_BY_ID: Readonly<Record<TileId, TileDef>> = Object.fromEntries(
  TILES.map((t) => [t.id, t]),
) as Record<TileId, TileDef>;

/** Default visual for a sim behavior, for levels built without content art (tests, tools). */
export const DEFAULT_ART: Readonly<Record<Tile, TileId>> = {
  [Tile.Ground]: 'path',
  [Tile.Grass]: 'grass',
  [Tile.Mowed]: 'grass',
  [Tile.Obstacle]: 'rock',
  [Tile.TallGrass]: 'tallGrass',
  [Tile.Brush]: 'sticks',
  [Tile.FlowerBed]: 'flowerBed',
  [Tile.Mud]: 'mud',
  [Tile.Hedge]: 'shrub',
  [Tile.RoughGrass]: 'tallGrass',
  [Tile.Ornament]: 'gnome',
  [Tile.Hose]: 'hose',
  [Tile.HoseChewed]: 'hose',
  [Tile.Sprinkler]: 'sprinkler',
  [Tile.Molehill]: 'grass',
  [Tile.Thorns]: 'thorns',
  [Tile.Hive]: 'beehive',
};

/** Whether a lawn has tall grass (or wheat) anywhere. */
export function hasTallGrass(art: readonly TileId[]): boolean {
  return art.some((id) => TILE_BY_ID[id].kind === Tile.TallGrass);
}

/** "1 pass" / "3 passes". */
export function passesText(n: number): string {
  return n === 1 ? '1 pass' : `${n} passes`;
}

/** What tall grass takes with a mower of this bite, e.g. "Tall grass: 3 passes". */
export function tallGrassNote(bite: number): string {
  return `Tall grass: ${passesText(tallGrassPasses(bite))}`;
}

/**
 * Break tiers (owner, 2026-10-04): how strong a mower must be to drive through a lawn item and
 * smash it (see each mower's `breakPower`: reel 0, push and self-propelled 1, zero-turn 2, lawn
 * tractor 3, Fairway Mower 4). A weaker mower crashes into it like a rock. `null`: not breakable
 * (every mower crashes into it, or it is not in the way at all). Every tile is listed, so a new
 * one cannot be added without deciding.
 */
export const BREAK_TIER: Readonly<Record<TileId, number | null>> = {
  // Tier 0, any mower: hollow plastic, it goes flying.
  cone: 0,
  // Tier 1, push and up: a small resin figure; a springy pole.
  gnome: 1,
  cornerFlag: 1,
  // Tier 2, zero-turn and up: a bowl on a pedestal tips over; a bin is knocked flat; a pin snaps.
  birdBath: 2,
  bin: 2,
  flag: 2,
  // Tier 3, the heavy riding mowers: a bush flattened, a wooden bench splintered, a planter cracked.
  bench: 3,
  shrub: 3,
  topiary: 3,
  urn: 3,
  // Tier 4, only the big gang mower: a metal box on a post.
  ballWasher: 4,
  // Never: stone, metal, living trees, buildings and fences (and a headstone, out of respect; a
  // dog house might have a dog in it).
  rock: null,
  tree: null,
  bigTree: null,
  deadTree: null,
  log: null,
  house: null,
  barn: null,
  fence: null,
  hedge: null,
  mazeHedge: null,
  lampPost: null,
  lantern: null,
  headstone: null,
  statue: null,
  fountain: null,
  trough: null,
  hayBale: null,
  dogHouse: null,
  goal: null,
  pen: null,
  water: null,
  // Not in the way: driven over (mowed, trampled, chewed, broken or plowed as before).
  grass: null,
  tallGrass: null,
  path: null,
  flowerBed: null,
  mud: null,
  sticks: null,
  sand: null,
  wheat: null,
  crops: null,
  hose: null,
  sprinkler: null,
  showLawn: null,
  judgesTable: null,
  penGate: null,
  roboDock: null,
  green: null,
  thorns: null,
  cobble: null,
  bridge: null,
  fieldLine: null,
  meadowGrass: null,
  // Any mower smashes a hive (and pays for it in a sting and coins).
  beehive: 0,
};

/** Lawn items some mower can smash through (each with its {@link BREAK_TIER}). */
export const BREAKABLES: readonly TileId[] = TILES.filter((t) => BREAK_TIER[t.id] !== null).map(
  (t) => t.id,
);

/** The biggest thing each break tier reaches, for short summaries ("Smashes up to benches"). */
const TIER_NAMES = ['cones', 'gnomes', 'bird baths', 'benches', 'ball washers'] as const;

/** What a mower with this break power smashes through, in a few words. */
export function breakSummary(breakPower: number): string {
  if (breakPower <= 0) return 'Smashes only cones';
  if (breakPower === 1) return 'Smashes cones, gnomes';
  return `Smashes up to ${TIER_NAMES[Math.min(breakPower, TIER_NAMES.length - 1)]}`;
}

/** Smaller lawn decorations (the level card warns about smashing them). */
export const DECORATIONS: readonly TileId[] = BREAKABLES;

/**
 * The lawn under a big tree's crown, relative to the trunk: the 5 x 5 square round it without its
 * corners (and without the trunk). Kept free of obstacles, since the crown hides it.
 */
export const BIG_TREE_CROWN: readonly (readonly [number, number])[] = (() => {
  const out: [number, number][] = [];
  for (let oy = -2; oy <= 2; oy++) {
    for (let ox = -2; ox <= 2; ox++) {
      if ((ox === 0 && oy === 0) || (Math.abs(ox) === 2 && Math.abs(oy) === 2)) continue;
      out.push([ox, oy]);
    }
  }
  return out;
})();
